import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../utils/api';
import { useLanguage } from '../context/LanguageContext';

const ViewAllPattasDialog = ({ open, onClose, onPreview }) => {
  const theme = useTheme();
  const { lang } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedYearName, setSelectedYearName] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingYears, setFetchingYears] = useState(false);
  const [error, setError] = useState('');
  const [loadingCertificateId, setLoadingCertificateId] = useState(null);

  // Fetch available years on dialog open
  useEffect(() => {
    if (open) {
      fetchAvailableYears();
    }
  }, [open]);

  const fetchAvailableYears = async () => {
    try {
      setFetchingYears(true);
      const response = await api.get('/api/admin-config/all-years');
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Sort years in descending order (newest first)
          const sortedYears = result.data.sort((a, b) => {
            if (!a.year_id || !b.year_id) return 0;
            return b.year_id - a.year_id;
          });
          setYears(sortedYears);
          
          // Auto-select the most recent year
          if (sortedYears.length > 0) {
            setSelectedYear(sortedYears[0].year_id);
            setSelectedYearName(sortedYears[0].year);
          }
        }
      } else {
        throw new Error('Failed to fetch years');
      }
    } catch (err) {
      console.error('Error fetching years:', err);
      setError('Failed to fetch available years');
    } finally {
      setFetchingYears(false);
    }
  };

  const handleYearChange = (e) => {
    const yearId = e.target.value;
    setSelectedYear(yearId);
    
    // Find the corresponding year name
    const year = years.find(y => y.year_id === yearId);
    setSelectedYearName(year?.year || '');
  };

  const fetchCertificates = async () => {
    if (!selectedYear) {
      setError('Please select a year');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/certificate/by-year/${selectedYear}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCertificates(result.data || []);
        }
      } else {
        throw new Error('Failed to fetch certificates');
      }
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setError('Failed to fetch certificates');
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  // Format kramantham type for display
  const formatKramanthamType = (type) => {
    const typeMap = {
      'moolam': 'मूलम्',
      'saamaanya_kramamu': 'क्रमान्तम्',
      'saamaanya_ghanamu': 'घनान्तम्',
      'vishesha_kramamu': 'विशेष क्रमान्तम्',
      'vishesha_ghanamu': 'विशेष घनान्तम्'
    };
    return typeMap[type] || type;
  };

  // Handle preview of certificate - pass to parent via onPreview callback
  const handlePreview = async (cert) => {
    try {
      console.log('Fetching preview for certificate:', cert.certificate_id);
      setLoadingCertificateId(cert._id);
      
      // Fetch certificate with signatures using certificate_id (not MongoDB _id)
      const response = await api.get(`/api/certificate/with-signatures/${cert.certificate_id}`);
      
      console.log('Preview fetch response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Preview fetch result:', result);
        
        if (result.success) {
          console.log('Preview data received, calling onPreview callback');
          // If parent has onPreview handler, use it to open preview in parent context
          if (onPreview && typeof onPreview === 'function') {
            onPreview(result.data);
          } else {
            // Fallback: log error if no callback provided
            console.warn('onPreview callback not provided to ViewAllPattasDialog');
            setError('Preview functionality not available');
          }
        } else {
          console.error('Preview fetch not successful:', result.error || 'Unknown error');
          setError(result.error || 'Failed to fetch certificate');
        }
      } else {
        throw new Error(`API error: ${response.status}`);
      }
    } catch (err) {
      console.error('Error loading certificate preview:', err);
      setError(err.message || 'Failed to load certificate preview');
    } finally {
      setLoadingCertificateId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6" sx={{ m: 0, fontWeight: 600 }}>
          {lang === 'sanskrit' 
              ? 'अद्यापि जारिकृतानि पट्टानि' 
              : 'ఇప్పటివరకు జారీ చేసిన పట్టాల నివేదిక'}
        </Typography>
        <IconButton onClick={onClose} disabled={loading || fetchingYears}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* Year Selection Section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'flex-end',
            }}
          >
            <FormControl
              sx={{ minWidth: isMobile ? '100%' : 250 }}
              disabled={fetchingYears}
            >
              <InputLabel id="year-select-label">Select Year</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={selectedYear}
                label="Select Year"
                onChange={handleYearChange}
              >
                {years.map(year => (
                  <MenuItem key={year._id} value={year.year_id}>
                    {year.year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={fetchCertificates}
              disabled={!selectedYear || loading}
              sx={{ height: 56, minWidth: 150 }}
              endIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              {loading ? 'Loading...' : 'Fetch Pattas'}
            </Button>
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* No Selection Message */}
        {!loading && certificates.length === 0 && selectedYear && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              No pattas issued in {selectedYearName}
            </Typography>
          </Box>
        )}

        {/* Pattas Grid */}
        {certificates.length > 0 && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Pattas Issued: {certificates.length}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {certificates.map(cert => (
                <Grid item xs={12} sm={6} md={4} key={cert._id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-4px)',
                      },
                      background: 'linear-gradient(135deg, #fffaf1 0%, #c3cfe2 100%)',
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      {/* Certificate ID */}
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          color: '#666',
                          fontWeight: 500,
                        }}
                      >
                        Cert: {cert.certificate_id}
                      </Typography>

                      {/* Vatu Name - Header */}
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1.5,
                          fontWeight: 700,
                          color: '#2c1810',
                          fontSize: '1.1rem',
                        }}
                      >
                        {cert.vatu_name || 'N/A'}
                      </Typography>

                      {/* Preview Button */}
                      <Box sx={{ mb: 1.5 }}>
                        <Tooltip title="Preview Certificate">
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(cert)}
                            disabled={loadingCertificateId === cert._id}
                            sx={{
                              color: '#1976d2',
                              p: 0.5,
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Guruvu Name */}
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 600,
                              color: '#666',
                              mb: 0.3,
                            }}
                          >
                            Guruvu:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            {cert.guruvu_name || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Kramantham Type */}
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 600,
                              color: '#666',
                              mb: 0.3,
                            }}
                          >
                            Kramantham:
                          </Typography>
                          <Chip
                            label={formatKramanthamType(cert.kramantham_type)}
                            size="small"
                            sx={{
                              backgroundColor: '#e8d7c3',
                              color: '#8b6f47',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Box>

                        {/* Gothram */}
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 600,
                              color: '#666',
                              mb: 0.3,
                            }}
                          >
                            Gothram:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                            {cert.gothram || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Shreni */}
                        {cert.shreni && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight: 600,
                                color: '#666',
                                mb: 0.3,
                              }}
                            >
                              Category:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                              {cert.shreni}
                            </Typography>
                          </Box>
                        )}

                        {/* Issue Date */}
                        {cert.issue_date && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight: 600,
                                color: '#999',
                                mb: 0.3,
                              }}
                            >
                              Issued:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#999', fontWeight: 500 }}
                            >
                              {new Date(cert.issue_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewAllPattasDialog;
